/**
 * Event tracking functions for Google Ads conversion tracking
 * All events follow Google's recommended naming conventions (snake_case)
 */

import { pushEvent, sendGA4Event } from './dataLayer.js';
import { getAttribution, getPlatform, extractDomain } from './attribution.js';

/**
 * Track enhanced page view with attribution data
 * Should be called on initial page load
 */
export const trackPageViewEnhanced = () => {
  try {
    const attribution = getAttribution();
    
    pushEvent('page_view_enhanced', {
      page_type: 'calculator',
      platform: attribution.platform || 'direct',
      utm_source: attribution.utm_source || null,
      utm_medium: attribution.utm_medium || null,
      utm_campaign: attribution.utm_campaign || null,
      utm_content: attribution.utm_content || null,
      utm_term: attribution.utm_term || null,
      gclid: attribution.gclid || null,
    });
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Track calculator interactions (dropdowns, sliders, toggles)
 * @param {string} fieldName - Name of the field changed
 * @param {string|number|boolean} newValue - The new value
 */
export const trackCalculatorInteraction = (fieldName, newValue) => {
  try {
    pushEvent('calculator_interaction', {
      field_name: fieldName,
      new_value: String(newValue),
      platform: getPlatform(),
    });
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Track URL analyze action
 * @param {string} url - The URL being analyzed (will be converted to domain only)
 */
export const trackUrlAnalyze = (url) => {
  try {
    const domain = extractDomain(url);
    
    pushEvent('url_analyze', {
      domain: domain,
      platform: getPlatform(),
    });
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Track CTA button clicks
 * @param {string} ctaName - Name of the CTA (get_started, book_call, explore_aor, book_time_continue)
 * @param {Object} options - Additional options
 * @param {number} options.quoteValue - Current quote value in dollars
 * @param {string} options.platform - Selected platform
 */
export const trackCtaClick = (ctaName, options = {}) => {
  try {
    const eventData = {
      cta_name: ctaName,
      platform: options.platform || getPlatform(),
    };
    
    // Include quote value if provided (for relevant CTAs)
    if (options.quoteValue !== undefined) {
      eventData.quote_value = options.quoteValue;
    }
    
    pushEvent('cta_click', eventData);
  } catch (error) {
    // Silent fail in production
  }
};

// Track if form_start has already been fired this session
let formStartFired = false;

/**
 * Track form start (first interaction with form)
 * Only fires once per session
 */
export const trackFormStart = () => {
  try {
    // Only fire once per session
    if (formStartFired) {
      return;
    }
    
    // Also check sessionStorage in case of page reload
    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem('azai_form_started')) {
        formStartFired = true;
        return;
      }
      sessionStorage.setItem('azai_form_started', 'true');
    }
    
    formStartFired = true;
    
    pushEvent('form_start', {
      platform: getPlatform(),
    });
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Track form submission attempt
 * @param {Object} formData - Non-PII form data
 * @param {string} formData.platform - Selected platform
 * @param {number} formData.quoteValue - Quote value in dollars
 * @param {string} formData.companySize - Company size tier
 * @param {string} formData.industry - Industry selection
 * @param {boolean} formData.hasPhone - Whether phone was provided
 * @param {boolean} formData.hasMessage - Whether message was provided
 * @param {boolean} formData.interestedInAor - AOR checkbox state
 */
export const trackFormSubmit = (formData) => {
  try {
    const eventData = {
      platform: formData.platform || getPlatform(),
      quote_value: formData.quoteValue,
      company_size: formData.companySize,
      industry: formData.industry,
      has_phone: Boolean(formData.hasPhone),
      has_message: Boolean(formData.hasMessage),
      interested_in_aor: Boolean(formData.interestedInAor),
      // Include attribution for conversion tracking
      ...getAttributionForEvent(),
    };

    // Push to dataLayer for GTM
    pushEvent('form_submit', eventData);

    // Also fire via gtag for direct GA4 tracking
    sendGA4Event('form_submit', eventData);
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Track successful form submission
 */
export const trackFormSubmitSuccess = () => {
  try {
    const eventData = {
      platform: getPlatform(),
      ...getAttributionForEvent(),
    };

    // Push to dataLayer for GTM
    pushEvent('form_submit_success', eventData);

    // Also fire via gtag for direct GA4 tracking
    sendGA4Event('form_submit_success', eventData);
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Track generate_lead event - GA4 recommended event for lead generation
 * This is a GA4 recommended event that is automatically recognized for conversion tracking.
 * See: https://developers.google.com/analytics/devguides/collection/ga4/reference/events#generate_lead
 * @param {Object} formData - Lead data (non-PII)
 * @param {number} formData.quoteValue - The estimated project value
 * @param {string} formData.platform - Selected CMS platform
 * @param {string} formData.companySize - Company size tier
 * @param {string} formData.industry - Industry selection
 */
export const trackGenerateLead = (formData) => {
  try {
    const attribution = getAttributionForEvent();

    // GA4 recommended parameters for generate_lead
    const ga4Params = {
      currency: 'USD',
      value: formData.quoteValue || 0,
    };

    // Custom parameters for richer reporting
    const customParams = {
      platform: formData.platform || getPlatform(),
      company_size: formData.companySize,
      industry: formData.industry,
      ...attribution,
    };

    // Push to dataLayer for GTM (combined params)
    pushEvent('generate_lead', {
      ...ga4Params,
      ...customParams,
    });

    // Also fire via gtag for direct GA4 tracking (GA4 recommended format)
    sendGA4Event('generate_lead', {
      ...ga4Params,
      ...customParams,
    });
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Track form submission error
 * @param {string} errorType - Type of error (network, validation, server, etc.)
 */
export const trackFormSubmitError = (errorType) => {
  try {
    pushEvent('form_submit_error', {
      error_type: errorType,
      platform: getPlatform(),
    });
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Get attribution data formatted for event inclusion
 * @returns {Object} - Attribution data for events
 */
const getAttributionForEvent = () => {
  try {
    const attribution = getAttribution();
    return {
      gclid: attribution.gclid || null,
      utm_source: attribution.utm_source || null,
      utm_medium: attribution.utm_medium || null,
      utm_campaign: attribution.utm_campaign || null,
    };
  } catch (error) {
    return {};
  }
};

export default {
  trackPageViewEnhanced,
  trackCalculatorInteraction,
  trackUrlAnalyze,
  trackCtaClick,
  trackFormStart,
  trackFormSubmit,
  trackFormSubmitSuccess,
  trackFormSubmitError,
  trackGenerateLead,
};
